import { useState, useEffect } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';

const GET_USER_WHATSAPP_SESSIONS = gql`
  query GetUserWhatsAppSessions($userId: String!) {
    userWhatsappSessions(userId: $userId) {
      sessionId
      userId
      phoneNumber
      status
      qrCode
      pairingCode
      connectionType
      lastConnected
      createdAt
      updatedAt
    }
  }
`;

const CREATE_WHATSAPP_SESSION = gql`
  mutation CreateWhatsAppSession($input: CreateWhatsAppSessionInput!) {
    createWhatsappSession(input: $input) {
      sessionId
      userId
      status
      qrCode
      pairingCode
      connectionType
    }
  }
`;

const DISCONNECT_WHATSAPP_SESSION = gql`
  mutation DisconnectWhatsAppSession($sessionId: String!) {
    disconnectWhatsappSession(sessionId: $sessionId)
  }
`;

const DELETE_WHATSAPP_SESSION = gql`
  mutation DeleteWhatsAppSession($sessionId: String!) {
    deleteWhatsappSession(sessionId: $sessionId)
  }
`;

const SEND_WHATSAPP_MESSAGE = gql`
  mutation SendWhatsAppMessage($input: SendMessageInput!) {
    sendWhatsappMessage(input: $input) {
      success
      message
    }
  }
`;

const WhatsAppConnect = () => {
  const user = useSelector((state) => state.auth.user);
  const [sessionId, setSessionId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [usePairingCode, setUsePairingCode] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');

  console.log('WhatsAppConnect - User from Redux:', user);
  console.log('WhatsAppConnect - User ID:', user?.id);

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(GET_USER_WHATSAPP_SESSIONS, {
    variables: { userId: user?.id?.toString() || '1' },
    skip: !user?.id,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      console.log('WhatsApp Sessions Query Completed:', data);
    },
    onError: (error) => {
      console.error('WhatsApp Sessions Query Error:', error);
    }
  });

  const [createSession] = useMutation(CREATE_WHATSAPP_SESSION);
  const [disconnectSession] = useMutation(DISCONNECT_WHATSAPP_SESSION);
  const [deleteSession] = useMutation(DELETE_WHATSAPP_SESSION);
  const [sendMessage] = useMutation(SEND_WHATSAPP_MESSAGE);

  // Poll for QR code updates when a session is connecting
  useEffect(() => {
    const hasConnectingSession = data?.userWhatsappSessions?.some(
      s => s.status === 'connecting' || s.status === 'qr_ready'
    );

    if (hasConnectingSession) {
      startPolling(2000); // Poll every 2 seconds
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [data, startPolling, stopPolling]);

  const handleCreateSession = async (e) => {
    e.preventDefault();

    if (usePairingCode && !phoneNumber) {
      alert('Please enter phone number for pairing code method');
      return;
    }

    try {
      await createSession({
        variables: {
          input: {
            sessionId: sessionId || `session_${Date.now()}`,
            userId: user?.id?.toString() || '1',
            phoneNumber: usePairingCode ? phoneNumber : null,
            usePairingCode
          }
        }
      });
      setSessionId('');
      setPhoneNumber('');
      setUsePairingCode(false);
      setShowCreateForm(false);
      refetch();
    } catch (err) {
      console.error('Error creating session:', err);
      alert('Error creating session: ' + err.message);
    }
  };

  const handleDisconnect = async (sessionId) => {
    if (window.confirm('Are you sure you want to disconnect this WhatsApp session?')) {
      try {
        await disconnectSession({ variables: { sessionId } });
        refetch();
      } catch (err) {
        console.error('Error disconnecting session:', err);
        alert('Error disconnecting session: ' + err.message);
      }
    }
  };

  const handleDelete = async (sessionId) => {
    if (window.confirm('Are you sure you want to permanently delete this session? This will also delete all message logs.')) {
      try {
        await deleteSession({ variables: { sessionId } });
        refetch();
      } catch (err) {
        console.error('Error deleting session:', err);
        alert('Error deleting session: ' + err.message);
      }
    }
  };

  const handleSendTestMessage = async (e) => {
    e.preventDefault();
    if (!selectedSession) {
      alert('Please select a connected session');
      return;
    }

    try {
      const result = await sendMessage({
        variables: {
          input: {
            sessionId: selectedSession.sessionId,
            phoneNumber: testPhone,
            message: testMessage
          }
        }
      });

      if (result.data.sendWhatsappMessage.success) {
        alert('Message sent successfully!');
        setTestPhone('');
        setTestMessage('');
      } else {
        alert('Failed to send message: ' + result.data.sendWhatsappMessage.message);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Error sending message: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      connected: { text: 'Connected', class: 'status-connected' },
      connecting: { text: 'Connecting...', class: 'status-connecting' },
      qr_ready: { text: 'Scan QR Code', class: 'status-qr' },
      pairing_code_ready: { text: 'Enter Pairing Code', class: 'status-pairing' },
      disconnected: { text: 'Disconnected', class: 'status-disconnected' }
    };
    return badges[status] || badges.disconnected;
  };

  if (loading) return <div className="p-8 text-center text-gray-600">Loading WhatsApp sessions...</div>;
  if (error) return <div className="p-8 text-center text-red-600">Error: {error.message}</div>;

  const sessions = data?.userWhatsappSessions || [];

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto animate-in fade-in duration-700">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-1.5 h-6 bg-bulky-blue-500 rounded-full"></div>
            <h2 className="font-medium text-bulky-navy-800 tracking-tight uppercase leading-none">WhatsApp Numbers</h2>
          </div>
          <p className="text-bulky-navy-400 text-[9px] md:text-[10px] font-normal uppercase tracking-wider md:tracking-wider ml-4">Manage your connected numbers</p>
        </div>
        <button
          className="w-full sm:w-auto bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal px-8 py-4.5 rounded-2xl transition-all duration-300 shadow-xl shadow-bulky-blue-500/25 active:scale-95 flex items-center justify-center gap-3 text-[10px] uppercase tracking-wider"
          onClick={() => setShowCreateForm(true)}
        >
          <span className="material-icons-round text-lg">add_circle</span>
          <span>Add Number</span>
        </button>
      </div>

      {showCreateForm && (
        <div className="fixed inset-0 bg-bulky-navy-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4" onClick={() => setShowCreateForm(false)}>
          <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-300 border border-white/20" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-br from-bulky-navy-800 to-bulky-navy-950 p-8 md:p-12 text-center text-white relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-bulky-blue-500/10 rounded-bl-full animate-pulse"></div>
              <h2 className="font-medium text-white tracking-tight uppercase relative z-10 leading-none">Connect New Number</h2>
              <p className="text-bulky-blue-400 text-[10px] font-normal uppercase tracking-wider mt-3 relative z-10">Link your WhatsApp number</p>
              <button
                onClick={() => setShowCreateForm(false)}
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-xl bg-white/5 text-white/50 hover:text-white hover:bg-white/10 transition-all z-20"
              >
                <span className="material-icons-round text-xl">close</span>
              </button>
            </div>

            <form onSubmit={handleCreateSession} className="p-8 md:p-10 space-y-8">
              <div>
                <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Name (optional)</label>
                <input
                  type="text"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                  placeholder="e.g., My Business Number"
                  className="w-full px-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-[1.25rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800"
                />
              </div>

              <div>
                <label className="flex items-center gap-4 p-5 bg-bulky-blue-50 rounded-2xl cursor-pointer hover:bg-bulky-blue-100/50 transition-all group border border-bulky-blue-100/50">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={usePairingCode}
                      onChange={(e) => setUsePairingCode(e.target.checked)}
                      className="w-5 h-5 accent-bulky-blue-500 rounded-lg cursor-pointer"
                    />
                  </div>
                  <span className="text-[10px] font-normal text-bulky-navy-800 uppercase tracking-wider">Use Code instead of QR</span>
                </label>
              </div>

              {usePairingCode && (
                <div className="animate-in slide-in-from-top-4 duration-500">
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-3 ml-1">Phone Number</label>
                  <input
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="919876543210"
                    required
                    className="w-full px-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-[1.25rem] focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                  />
                  <p className="mt-3 text-[9px] text-bulky-blue-500 font-normal uppercase tracking-wider leading-none ml-1 flex items-center gap-1.5">
                    <span className="material-icons-round text-xs">info</span>
                    Include country code (e.g. 91 for India)
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button type="submit" className="flex-[2] bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal py-4.5 rounded-2xl transition-all shadow-xl shadow-bulky-blue-500/25 active:scale-95 text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-2">
                  <span className="material-icons-round text-lg">{usePairingCode ? 'key' : 'qr_code_2'}</span>
                  {usePairingCode ? 'Get Code' : 'Show QR Code'}
                </button>
                <button type="button" onClick={() => setShowCreateForm(false)} className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-500 font-normal py-4.5 rounded-2xl transition-all text-[10px] uppercase tracking-wider">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {sessions.map((session) => {
          const badge = getStatusBadge(session.status);
          const statusColors = {
            'status-connected': 'bg-emerald-50 text-emerald-600 border-emerald-100 shadow-[0_0_15px_-5px_rgba(16,185,129,0.3)]',
            'status-connecting': 'bg-amber-50 text-amber-600 border-amber-100 shadow-[0_0_15px_-5px_rgba(245,158,11,0.3)]',
            'status-qr': 'bg-bulky-blue-50 text-bulky-blue-600 border-bulky-blue-100 shadow-[0_0_15px_-5px_rgba(40,92,237,0.3)]',
            'status-pairing': 'bg-blue-50 text-blue-600 border-blue-100 shadow-[0_0_15px_-5px_rgba(59,130,246,0.3)]',
            'status-disconnected': 'bg-red-50 text-red-600 border-red-100 shadow-[0_0_15px_-5px_rgba(239,68,68,0.3)]'
          };
          return (
            <div key={session.sessionId} className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-bulky-blue-500/10 transition-all duration-500 group flex flex-col relative">
              <div className="absolute top-0 right-0 w-24 h-24 bg-bulky-blue-500/5 rounded-full -mr-12 -mt-12 transition-transform duration-700 group-hover:scale-150"></div>

              <div className="bg-gray-50/50 p-6 border-b border-gray-50 flex items-center justify-between gap-4 relative z-10">
                <div className="overflow-hidden flex-1">
                  <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider mb-1.5 leading-none">Number Name</p>
                  <h2 className="font-medium text-bulky-navy-800 text-xs truncate uppercase tracking-tighter">{session.sessionId}</h2>
                </div>
                <div className={`shrink-0 px-3.5 py-1.5 rounded-xl text-[9px] font-normal uppercase tracking-wider border ${statusColors[badge.class]}`}>
                  <span className="flex items-center gap-1.5">
                    {session.status === 'connected' && <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>}
                    {badge.text}
                  </span>
                </div>
              </div>

              <div className="p-8 flex-1 flex flex-col relative z-10">
                <div className="space-y-6 flex-1">
                  {session.phoneNumber && (
                    <div className="flex items-center gap-4 group/item">
                      <div className="w-11 h-11 rounded-xl bg-bulky-blue-50 flex items-center justify-center text-bulky-blue-500 group-hover/item:bg-bulky-blue-500 group-hover/item:text-white transition-all duration-300 shadow-inner">
                        <span className="material-icons-round text-lg">phone_iphone</span>
                      </div>
                      <div>
                        <p className="text-[9px] font-normal text-gray-400 uppercase tracking-wider leading-none mb-1.5">Phone Number</p>
                        <p className="text-bulky-navy-800 font-normal text-sm tracking-tight">{session.phoneNumber}</p>
                      </div>
                    </div>
                  )}

                  {session.qrCode && session.status === 'qr_ready' && (
                    <div className="bg-gray-50/50 p-6 md:p-8 rounded-[2rem] border-2 border-dashed border-gray-200 group-hover:border-bulky-blue-200 transition-colors">
                      <p className="text-[9px] text-gray-400 font-normal uppercase tracking-[0.2em] mb-6 text-center">Scan QR with WhatsApp</p>
                      <div className="bg-white p-4 rounded-2xl shadow-xl border border-gray-100 transform transition-transform group-hover:scale-[1.02]">
                        <img src={session.qrCode} alt="QR Code" className="w-full h-auto rounded-xl mx-auto mix-blend-multiply" />
                      </div>
                    </div>
                  )}

                  {session.pairingCode && session.status === 'pairing_code_ready' && (
                    <div className="bg-bulky-blue-50 p-6 md:p-8 rounded-[2rem] border-2 border-bulky-blue-200 shadow-xl shadow-bulky-blue-500/10">
                      <p className="text-[9px] text-bulky-blue-600 font-normal uppercase tracking-wider mb-6 text-center">Enter this code</p>
                      <div className="text-3xl md:text-4xl font-display font-normal text-bulky-blue-600 text-center my-4 tracking-[0.2em] break-all">{session.pairingCode}</div>
                      <div className="mt-6 pt-6 border-t border-bulky-blue-200/50">
                        <p className="text-[9px] text-bulky-blue-400/70 text-center leading-relaxed font-normal uppercase tracking-tight">
                          Linked Devices → Link a Device → Link with phone number
                        </p>
                      </div>
                    </div>
                  )}

                  {session.lastConnected && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100/50">
                      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-gray-300 shadow-sm">
                        <span className="material-icons-round text-lg">history</span>
                      </div>
                      <div>
                        <p className="text-[8px] font-normal text-gray-400 uppercase tracking-wider mb-1 leading-none">Last Connected</p>
                        <span className="text-bulky-navy-800 font-normal text-[10px] uppercase tracking-tighter">{new Date(parseInt(session.lastConnected)).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-8">
                  {session.status === 'connected' && (
                    <button
                      className="flex-1 bg-bulky-navy-800 hover:bg-bulky-navy-900 text-white font-normal py-4.5 px-3 rounded-2xl transition-all shadow-xl active:scale-95 flex items-center justify-center gap-2 text-[9px] uppercase tracking-wider"
                      onClick={() => setSelectedSession(session)}
                    >
                      <span className="material-icons-round text-sm">send</span>
                      <span>Send Test</span>
                    </button>
                  )}
                  {session.status !== 'disconnected' && (
                    <button
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white font-normal py-4.5 px-3 rounded-2xl transition-all shadow-xl active:scale-95 flex items-center justify-center gap-2 text-[9px] uppercase tracking-wider"
                      onClick={() => handleDisconnect(session.sessionId)}
                    >
                      <span className="material-icons-round text-sm">power_off</span>
                      <span>Disconnect</span>
                    </button>
                  )}
                  {session.status === 'disconnected' && (
                    <button
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white font-normal py-4.5 px-3 rounded-2xl transition-all shadow-xl active:scale-95 flex items-center justify-center gap-2 text-[9px] uppercase tracking-wider"
                      onClick={() => handleDelete(session.sessionId)}
                    >
                      <span className="material-icons-round text-sm">delete</span>
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {sessions.length === 0 && (
          <div className="col-span-full py-24 md:py-32 text-center bg-white rounded-[3rem] border-2 border-dashed border-gray-100 px-6">
            <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-8 text-gray-200">
              <span className="material-icons-round text-5xl">phone_disabled</span>
            </div>
            <h2 className="font-medium">No WhatsApp Numbers</h2>
            <p className="text-gray-400 text-[10px] font-normal uppercase tracking-[0.2em] mt-3 mb-10 max-w-xs mx-auto">You haven't connected any WhatsApp numbers yet.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-bulky-navy-800 text-white font-normal px-10 py-5 rounded-2xl hover:bg-bulky-blue-500 transition-all shadow-xl shadow-black/10 active:scale-95 text-[10px] uppercase tracking-[0.2em]"
            >
              Add Your First Number
            </button>
          </div>
        )}
      </div>

      {selectedSession && (
        <div className="mt-16 bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-blue-50 animate-in slide-in-from-bottom-8 duration-700 relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-2 h-full bg-bulky-blue-500"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-bulky-blue-500/5 rounded-full -mr-48 -mt-48 blur-3xl opacity-50 group-hover:scale-110 transition-transform duration-1000"></div>

          <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12">
              <div className="flex items-start md:items-center gap-5">
                <div className="w-14 h-14 bg-bulky-blue-50 rounded-2xl flex items-center justify-center text-bulky-blue-500 shadow-inner group-hover:scale-110 transition-transform">
                  <span className="material-icons-round text-3xl">sensors</span>
                </div>
                <div>
                  <h2 className="font-medium text-bulky-navy-800 tracking-tight uppercase leading-none mb-3">Send Test Message</h2>
                  <p className="text-bulky-navy-400 text-[10px] font-normal uppercase tracking-[0.3em] flex items-center gap-2">
                    <span className="w-1 h-1 bg-bulky-blue-500 rounded-full"></span>
                    Testing <span className="text-bulky-blue-500">{selectedSession.sessionId}</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 px-5 py-2.5 bg-emerald-50 rounded-2xl border border-emerald-100 shadow-sm">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                <span className="text-[10px] font-normal text-emerald-600 uppercase tracking-wider">Connected</span>
              </div>
            </div>

            <form onSubmit={handleSendTestMessage} className="space-y-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div className="relative group/input">
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1">Phone Number</label>
                  <div className="relative">
                    <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within/input:text-bulky-blue-500 transition-colors">contact_phone</span>
                    <input
                      type="text"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="919876543210"
                      required
                      className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 shadow-sm"
                    />
                  </div>
                </div>
                <div className="relative group/input">
                  <label className="block text-[10px] font-normal text-bulky-navy-400 uppercase tracking-wider mb-4 ml-1">Message</label>
                  <div className="relative">
                    <span className="material-icons-round absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within/input:text-bulky-blue-500 transition-colors">description</span>
                    <textarea
                      value={testMessage}
                      onChange={(e) => setTestMessage(e.target.value)}
                      placeholder="Type your test message..."
                      rows="1"
                      required
                      className="w-full pl-14 pr-6 py-4.5 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-bulky-blue-500 focus:bg-white focus:outline-none transition-all font-normal text-bulky-navy-800 resize-none shadow-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-50">
                <button type="submit" className="flex-[2] bg-bulky-blue-500 hover:bg-bulky-blue-600 text-white font-normal py-5 rounded-[1.5rem] transition-all shadow-2xl shadow-bulky-blue-500/30 active:scale-[0.98] flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.2em]">
                  <span className="material-icons-round text-lg">send</span>
                  <span>Send Message</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSession(null)}
                  className="flex-1 bg-gray-50 hover:bg-gray-100 text-gray-500 font-normal py-5 rounded-[1.5rem] transition-all text-[10px] uppercase tracking-wider border border-gray-100"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppConnect;
